import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  User, Mail, Phone, Wallet, Calendar, MapPin, Globe, 
  Camera, Upload, Shield, CheckCircle, XCircle, Clock,
  FileText, AlertTriangle, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import api from '../../lib/api';

const KYC_DOCUMENT_TYPES = [
  { value: 'passport', label: 'Passport' },
  { value: 'national_id', label: 'National ID Card' },
  { value: 'drivers_license', label: "Driver's License" },
];

export default function UserProfile() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  
  // Profile form state
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    wallet_address: '',
    date_of_birth: '',
    country: '',
    address: '',
  });
  
  // KYC form state
  const [kycData, setKycData] = useState({
    document_type: 'national_id',
    document_front: '',
    document_back: '',
    selfie: '',
  });
  const [submittingKyc, setSubmittingKyc] = useState(false);
  
  const frontInputRef = useRef(null);
  const backInputRef = useRef(null);
  const selfieInputRef = useRef(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await api.get('/user/profile');
      setProfile(res.data);
      setFormData({
        full_name: res.data.full_name || '',
        phone: res.data.phone || '',
        wallet_address: res.data.wallet_address || '',
        date_of_birth: res.data.date_of_birth || '',
        country: res.data.country || '',
        address: res.data.address || '',
      });
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('/user/profile', formData);
      toast.success('Profile updated successfully');
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleFileSelect = (field, e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setKycData(prev => ({ ...prev, [field]: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleKycSubmit = async (e) => {
    e.preventDefault();
    
    if (!kycData.document_front) {
      toast.error('Please upload document front image');
      return;
    }
    if (kycData.document_type !== 'passport' && !kycData.document_back) {
      toast.error('Please upload document back image');
      return;
    }
    if (!kycData.selfie) {
      toast.error('Please upload a selfie');
      return;
    }
    
    setSubmittingKyc(true);
    try {
      await api.post('/user/kyc/submit', kycData);
      toast.success('KYC documents submitted successfully');
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit KYC');
    } finally {
      setSubmittingKyc(false);
    }
  };

  const getKycStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-500/20 text-green-400 text-xs font-medium">
            <CheckCircle className="w-3.5 h-3.5" /> Verified
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/20 text-amber-400 text-xs font-medium">
            <Clock className="w-3.5 h-3.5" /> Pending Review
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500/20 text-red-400 text-xs font-medium">
            <XCircle className="w-3.5 h-3.5" /> Rejected
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-zinc-500/20 text-zinc-400 text-xs font-medium">
            <AlertTriangle className="w-3.5 h-3.5" /> Not Submitted
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-amber-500/20 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="profile-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center text-black text-2xl font-bold">
            {profile?.full_name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          {profile?.kyc_status === 'approved' && (
            <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-green-500 flex items-center justify-center border-2 border-black">
              <CheckCircle className="w-4 h-4 text-white" />
            </div>
          )}
        </div>
        <div>
          <h1 className="text-2xl text-white font-medium">{profile?.full_name}</h1>
          <p className="text-sm text-zinc-500">{profile?.email}</p>
          <div className="mt-2">{getKycStatusBadge(profile?.kyc_status)}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10 pb-2">
        <button
          onClick={() => setActiveTab('profile')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'profile' 
              ? 'bg-amber-500/20 text-amber-400' 
              : 'text-zinc-400 hover:text-white'
          }`}
          data-testid="tab-profile"
        >
          <User className="w-4 h-4 inline mr-2" />
          Profile
        </button>
        <button
          onClick={() => setActiveTab('kyc')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'kyc' 
              ? 'bg-amber-500/20 text-amber-400' 
              : 'text-zinc-400 hover:text-white'
          }`}
          data-testid="tab-kyc"
        >
          <Shield className="w-4 h-4 inline mr-2" />
          KYC Verification
        </button>
      </div>

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-6"
        >
          <h2 className="text-lg text-white font-medium mb-6">Personal Information</h2>
          
          <form onSubmit={handleProfileUpdate} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-zinc-400 mb-2 block">Full Name</label>
                <div className="input-glass rounded-lg flex items-center px-3 py-2.5 gap-2">
                  <User className="w-4 h-4 text-zinc-500" />
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                    className="bg-transparent border-none outline-none text-sm text-white w-full"
                    data-testid="profile-fullname"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-xs text-zinc-400 mb-2 block">Email (Cannot change)</label>
                <div className="input-glass rounded-lg flex items-center px-3 py-2.5 gap-2 opacity-50">
                  <Mail className="w-4 h-4 text-zinc-500" />
                  <input
                    type="email"
                    value={profile?.email}
                    disabled
                    className="bg-transparent border-none outline-none text-sm text-white w-full cursor-not-allowed"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-xs text-zinc-400 mb-2 block">Phone Number</label>
                <div className="input-glass rounded-lg flex items-center px-3 py-2.5 gap-2">
                  <Phone className="w-4 h-4 text-zinc-500" />
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="bg-transparent border-none outline-none text-sm text-white w-full"
                    data-testid="profile-phone"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-xs text-zinc-400 mb-2 block">Date of Birth</label>
                <div className="input-glass rounded-lg flex items-center px-3 py-2.5 gap-2">
                  <Calendar className="w-4 h-4 text-zinc-500" />
                  <input
                    type="date"
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                    className="bg-transparent border-none outline-none text-sm text-white w-full"
                    data-testid="profile-dob"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-xs text-zinc-400 mb-2 block">Country</label>
                <div className="input-glass rounded-lg flex items-center px-3 py-2.5 gap-2">
                  <Globe className="w-4 h-4 text-zinc-500" />
                  <input
                    type="text"
                    value={formData.country}
                    onChange={(e) => setFormData({...formData, country: e.target.value})}
                    placeholder="Enter your country"
                    className="bg-transparent border-none outline-none text-sm text-white w-full placeholder-zinc-600"
                    data-testid="profile-country"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-xs text-zinc-400 mb-2 block">Address</label>
                <div className="input-glass rounded-lg flex items-center px-3 py-2.5 gap-2">
                  <MapPin className="w-4 h-4 text-zinc-500" />
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    placeholder="Enter your address"
                    className="bg-transparent border-none outline-none text-sm text-white w-full placeholder-zinc-600"
                    data-testid="profile-address"
                  />
                </div>
              </div>
            </div>
            
            <div>
              <label className="text-xs text-amber-400 mb-2 block">BLC Wallet Address (BEP-20)</label>
              <div className="input-glass rounded-lg flex items-center px-3 py-2.5 gap-2 border-amber-500/20">
                <Wallet className="w-4 h-4 text-amber-400" />
                <input
                  type="text"
                  value={formData.wallet_address}
                  onChange={(e) => setFormData({...formData, wallet_address: e.target.value})}
                  className="bg-transparent border-none outline-none text-sm text-white w-full font-mono"
                  data-testid="profile-wallet"
                />
              </div>
            </div>
            
            <div className="pt-4">
              <Button type="submit" disabled={saving} className="gold-btn" data-testid="profile-save">
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </motion.div>
      )}

      {/* KYC Tab */}
      {activeTab === 'kyc' && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* KYC Status Card */}
          <div className={`glass-card p-6 border-l-4 ${
            profile?.kyc_status === 'approved' ? 'border-green-500' :
            profile?.kyc_status === 'pending' ? 'border-amber-500' :
            profile?.kyc_status === 'rejected' ? 'border-red-500' : 'border-zinc-500'
          }`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg text-white font-medium">KYC Status</h2>
              {getKycStatusBadge(profile?.kyc_status)}
            </div>
            
            {profile?.kyc_status === 'approved' && (
              <p className="text-sm text-green-400">Your identity has been verified. You have full access to all platform features.</p>
            )}
            
            {profile?.kyc_status === 'pending' && (
              <div className="space-y-2">
                <p className="text-sm text-amber-400">Your documents are being reviewed. This usually takes 24-48 hours.</p>
                <p className="text-xs text-zinc-500">Submitted: {profile?.kyc_submitted_at ? new Date(profile.kyc_submitted_at).toLocaleString() : 'N/A'}</p>
              </div>
            )}
            
            {profile?.kyc_status === 'rejected' && (
              <div className="space-y-2">
                <p className="text-sm text-red-400">Your KYC was rejected. Please resubmit with valid documents.</p>
                <p className="text-xs text-zinc-400"><strong>Reason:</strong> {profile?.kyc_rejection_reason}</p>
              </div>
            )}
            
            {profile?.kyc_status === 'not_submitted' && (
              <p className="text-sm text-zinc-400">Complete KYC verification to unlock all platform features.</p>
            )}
          </div>
          
          {/* KYC Form - Only show if not approved or pending */}
          {(profile?.kyc_status === 'not_submitted' || profile?.kyc_status === 'rejected') && (
            <div className="glass-card p-6">
              <h2 className="text-lg text-white font-medium mb-6">Submit KYC Documents</h2>
              
              <form onSubmit={handleKycSubmit} className="space-y-6">
                {/* Document Type */}
                <div>
                  <label className="text-xs text-zinc-400 mb-2 block">Document Type</label>
                  <div className="grid grid-cols-3 gap-2">
                    {KYC_DOCUMENT_TYPES.map((type) => (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setKycData({...kycData, document_type: type.value})}
                        className={`p-3 rounded-lg text-xs font-medium transition-all ${
                          kycData.document_type === type.value
                            ? 'bg-amber-500 text-black'
                            : 'glass text-zinc-400 hover:text-white'
                        }`}
                      >
                        {type.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Document Images */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-zinc-400 mb-2 block">Document Front *</label>
                    {kycData.document_front ? (
                      <div className="relative">
                        <img src={kycData.document_front} alt="Front" className="w-full h-40 object-cover rounded-lg border border-white/10" />
                        <button
                          type="button"
                          onClick={() => setKycData({...kycData, document_front: ''})}
                          className="absolute top-2 right-2 p-1 bg-red-500 rounded-full text-white text-xs"
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <div
                        onClick={() => frontInputRef.current?.click()}
                        className="border-2 border-dashed border-white/10 rounded-lg p-6 text-center cursor-pointer hover:border-amber-500/50 transition-colors h-40 flex flex-col items-center justify-center"
                      >
                        <input ref={frontInputRef} type="file" accept="image/*" onChange={(e) => handleFileSelect('document_front', e)} className="hidden" />
                        <Upload className="w-8 h-8 text-zinc-500 mb-2" />
                        <p className="text-xs text-zinc-400">Click to upload</p>
                      </div>
                    )}
                  </div>
                  
                  {kycData.document_type !== 'passport' && (
                    <div>
                      <label className="text-xs text-zinc-400 mb-2 block">Document Back *</label>
                      {kycData.document_back ? (
                        <div className="relative">
                          <img src={kycData.document_back} alt="Back" className="w-full h-40 object-cover rounded-lg border border-white/10" />
                          <button
                            type="button"
                            onClick={() => setKycData({...kycData, document_back: ''})}
                            className="absolute top-2 right-2 p-1 bg-red-500 rounded-full text-white text-xs"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <div
                          onClick={() => backInputRef.current?.click()}
                          className="border-2 border-dashed border-white/10 rounded-lg p-6 text-center cursor-pointer hover:border-amber-500/50 transition-colors h-40 flex flex-col items-center justify-center"
                        >
                          <input ref={backInputRef} type="file" accept="image/*" onChange={(e) => handleFileSelect('document_back', e)} className="hidden" />
                          <Upload className="w-8 h-8 text-zinc-500 mb-2" />
                          <p className="text-xs text-zinc-400">Click to upload</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Selfie */}
                <div>
                  <label className="text-xs text-zinc-400 mb-2 block">Selfie with Document *</label>
                  <p className="text-xs text-zinc-500 mb-2">Take a photo of yourself holding your ID document.</p>
                  {kycData.selfie ? (
                    <div className="relative w-48">
                      <img src={kycData.selfie} alt="Selfie" className="w-full h-48 object-cover rounded-lg border border-white/10" />
                      <button
                        type="button"
                        onClick={() => setKycData({...kycData, selfie: ''})}
                        className="absolute top-2 right-2 p-1 bg-red-500 rounded-full text-white text-xs"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <div
                      onClick={() => selfieInputRef.current?.click()}
                      className="border-2 border-dashed border-white/10 rounded-lg p-6 text-center cursor-pointer hover:border-amber-500/50 transition-colors w-48 h-48 flex flex-col items-center justify-center"
                    >
                      <input ref={selfieInputRef} type="file" accept="image/*" onChange={(e) => handleFileSelect('selfie', e)} className="hidden" />
                      <Camera className="w-8 h-8 text-zinc-500 mb-2" />
                      <p className="text-xs text-zinc-400">Upload Selfie</p>
                    </div>
                  )}
                </div>
                
                <div className="pt-4">
                  <Button type="submit" disabled={submittingKyc} className="gold-btn" data-testid="kyc-submit">
                    {submittingKyc ? 'Submitting...' : 'Submit KYC Documents'}
                  </Button>
                </div>
              </form>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}
